import React, { useEffect, useState } from 'react';
import { User as UserIcon, ChevronDown, LogOut, RefreshCw, Plug, LogIn, Info, Smartphone, X } from 'lucide-react';
import { User, UserStatus } from '../../types';
import { useToast } from '../ui/Toaster';
import { AvatarCircle } from '../ui/AvatarCircle';

type DesktopUpdateCheck = {
  supported: boolean;
  platform?: 'windows' | 'linux';
  currentVersion: string;
  latestVersion: string;
  updateAvailable: boolean;
  file: string;
};

type DesktopUpdateDownloadProgress = {
  phase: 'idle' | 'downloading' | 'verifying' | 'installing' | 'error';
  percent: number | null;
  downloadedBytes: number;
  totalBytes: number;
  error?: string;
};

type Props = {
  currentUser: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenSettings: () => void;
  onOpenDevices: () => void;
  onLogout: () => void;
};

export const ProfileMenu: React.FC<Props> = ({ currentUser, open, onOpenChange, onOpenSettings, onOpenDevices, onLogout }) => (
  <ProfileMenuInner currentUser={currentUser} open={open} onOpenChange={onOpenChange} onOpenSettings={onOpenSettings} onOpenDevices={onOpenDevices} onLogout={onLogout} />
);

const ProfileMenuInner: React.FC<Props> = ({ currentUser, open, onOpenChange, onOpenSettings, onOpenDevices, onLogout }) => {
  const { addToast } = useToast();
  const isDesktop = typeof window !== 'undefined' && !!window.desktopApi;

  const [desktopVersion, setDesktopVersion] = useState<string>('');
  const [updateInfo, setUpdateInfo] = useState<DesktopUpdateCheck | null>(null);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [installingUpdate, setInstallingUpdate] = useState(false);
  const [clientVersionOpen, setClientVersionOpen] = useState(false);
  const [updateDownloadProgress, setUpdateDownloadProgress] = useState<DesktopUpdateDownloadProgress | null>(null);

  const getUpdateLabel = (info: DesktopUpdateCheck | null) => {
    if (!info) return '';
    if (!info.supported) return 'Обновления не поддерживаются';
    if (info.updateAvailable) return `Доступно: ${info.latestVersion}`;
    return 'Актуальная версия';
  };

  useEffect(() => {
    if (!isDesktop) return;
    (async () => {
      try {
        const v = await window.desktopApi?.getVersion();
        if (v) setDesktopVersion(String(v));
      } catch {}
    })();
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop) return;

    (async () => {
      try {
        const info = (await window.desktopApi?.checkForUpdates()) as any as DesktopUpdateCheck;
        if (info) setUpdateInfo(info);
      } catch {}
    })();
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop || !window.desktopApi?.onUpdateDownloadProgress) return;
    return window.desktopApi.onUpdateDownloadProgress((progress) => {
      setUpdateDownloadProgress(progress);
      if (progress.phase === 'downloading' || progress.phase === 'verifying' || progress.phase === 'installing') {
        setInstallingUpdate(true);
      }
      if (progress.phase === 'error') {
        setInstallingUpdate(false);
      }
    });
  }, [isDesktop]);

  useEffect(() => {
    if (!clientVersionOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setClientVersionOpen(false);
    };
    try {
      window.addEventListener('keydown', onKeyDown);
    } catch {}
    return () => {
      try {
        window.removeEventListener('keydown', onKeyDown);
      } catch {}
    };
  }, [clientVersionOpen]);

  const handleCheckUpdates = async () => {
    if (!isDesktop) return;
    setCheckingUpdates(true);
    setUpdateDownloadProgress(null);
    try {
      const info = (await window.desktopApi?.checkForUpdates()) as any as DesktopUpdateCheck;
      if (info) setUpdateInfo(info);

      if (!info?.supported) {
        setInstallingUpdate(false);
        addToast('Проверка обновлений не поддерживается на этой платформе', 'warning');
        return;
      }

      if (info?.updateAvailable) {
        addToast(`Найдено обновление до ${String(info.latestVersion || '')}. Устанавливаю…`, 'info');
        setInstallingUpdate(true);
        await window.desktopApi?.downloadAndInstallUpdate(String(info.file || ''));
        return;
      }

      setInstallingUpdate(false);
      addToast('У вас установлена последняя версия', 'success');
    } catch (e: any) {
      setInstallingUpdate(false);
      addToast(String(e?.message || 'Не удалось проверить обновления'), 'error');
    } finally {
      setCheckingUpdates(false);
    }
  };

  const handleSwitchServer = async () => {
    if (!isDesktop) return;
    onOpenChange(false);
    try {
      await window.desktopApi?.switchServer();
    } catch {}
  };

  const handleSwitchUser = async () => {
    if (!isDesktop) return;
    onOpenChange(false);
    try {
      await window.desktopApi?.switchUser();
    } catch {}
  };

  const getProgressTitle = (progress: DesktopUpdateDownloadProgress | null) => {
    if (!progress) return null;
    if (progress.phase === 'downloading') {
      if (typeof progress.percent === 'number') return `Скачивание обновления: ${progress.percent}%`;
      return 'Скачивание обновления…';
    }
    if (progress.phase === 'verifying') return 'Проверка загруженного файла…';
    if (progress.phase === 'installing') return 'Запуск установки…';
    if (progress.phase === 'error') return `Ошибка обновления: ${String(progress.error || 'unknown_error')}`;
    return null;
  };

  return (
    <div className="relative">
      <button 
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex items-center gap-3 hover:bg-gray-50 p-1 rounded-lg transition-colors"
        aria-label="Меню профиля"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="text-right">
          <p className="text-sm font-semibold text-gray-800 leading-none">{currentUser.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{currentUser.roleName || currentUser.role}</p>
        </div>
        <div className="relative">
          <AvatarCircle
            src={currentUser.avatar}
            name={currentUser.name}
            className="w-9 h-9 bg-brand-100 text-brand-600 border border-brand-200 transition-colors"
            fallbackBgClassName="bg-brand-100"
            fallbackTextClassName="text-brand-600 font-semibold"
            alt={currentUser.name}
          />
          <div className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-white rounded-full ${
            currentUser.status === UserStatus.ONLINE ? 'bg-green-500' : 'bg-gray-400'
          }`}></div>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => onOpenChange(false)}></div>
          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-100 z-20 py-1 animate-fade-in-up divide-y divide-gray-50">
            <div className="py-1">
              <button 
                type="button"
                onClick={() => { onOpenChange(false); onOpenSettings(); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <UserIcon className="w-4 h-4 text-gray-400" /> Настройки профиля
              </button>
              <button
                type="button"
                onClick={() => { onOpenChange(false); onOpenDevices(); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Smartphone className="w-4 h-4 text-gray-400" /> Устройства
              </button>
            </div>

            {isDesktop && (
              <div className="py-1">
                <button
                  type="button"
                  onClick={handleSwitchServer}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Plug className="w-4 h-4 text-gray-400" /> Сменить сервер
                </button>
                <button
                  type="button"
                  onClick={handleSwitchUser}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <LogIn className="w-4 h-4 text-gray-400" /> Сменить пользователя
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onOpenChange(false);
                    setClientVersionOpen(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Info className="w-4 h-4 text-gray-400" />
                  <span>Версия клиента</span>
                  <span className="ml-auto text-xs text-gray-400">{desktopVersion ? String(desktopVersion) : '—'}</span>
                </button>
              </div>
            )}

            <div className="py-1">
              <button 
                type="button"
                onClick={onLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" /> Выход
              </button>
            </div>
          </div>
        </>
      )}

      {isDesktop && clientVersionOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in-up" onClick={() => setClientVersionOpen(false)}>
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="client-version-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <h3 id="client-version-title" className="text-lg font-bold text-gray-900">Версия клиента</h3>
              <button
                type="button"
                onClick={() => setClientVersionOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Закрыть"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-3">
              <div className="text-sm text-gray-700">
                Текущая версия: <span className="font-semibold">{desktopVersion ? String(desktopVersion) : '—'}</span>
              </div>

              {updateInfo ? (
                <div className={`text-sm ${updateInfo.updateAvailable ? 'text-orange-600' : 'text-gray-600'}`}>
                  {getUpdateLabel(updateInfo)}
                </div>
              ) : (
                <div className="text-sm text-gray-500">Нажмите «Проверить обновления»</div>
              )}

              {updateInfo?.supported && updateInfo?.latestVersion && (
                <div className="text-xs text-gray-500">
                  Последняя версия: {String(updateInfo.latestVersion || '')}
                </div>
              )}

              {updateDownloadProgress && updateDownloadProgress.phase !== 'idle' && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 space-y-2">
                  <div className={`text-sm ${updateDownloadProgress.phase === 'error' ? 'text-red-600' : 'text-gray-700'}`}>
                    {getProgressTitle(updateDownloadProgress)}
                  </div>
                  {updateDownloadProgress.phase !== 'error' && (
                    <div className="space-y-2">
                      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${updateDownloadProgress.phase === 'installing' ? 'bg-green-500' : 'bg-brand-600'}`}
                          style={{ width: `${Math.max(6, updateDownloadProgress.percent ?? (updateDownloadProgress.phase === 'installing' ? 100 : 12))}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500">
                        {typeof updateDownloadProgress.percent === 'number'
                          ? `${updateDownloadProgress.percent}%`
                          : updateDownloadProgress.phase === 'verifying'
                            ? 'Проверка…'
                            : updateDownloadProgress.phase === 'installing'
                              ? 'Установка…'
                              : 'Подготовка…'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setClientVersionOpen(false)}
                className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
              >
                Закрыть
              </button>
              <button
                type="button"
                onClick={handleCheckUpdates}
                disabled={checkingUpdates || installingUpdate}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white shadow-sm transition-colors bg-brand-600 hover:bg-brand-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${checkingUpdates ? 'animate-spin' : ''}`} /> Проверить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
